import { useState } from 'react'
import { MessageSquare, Siren, UserSearch } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { Mackay66Panel } from './Mackay66Panel'
import { CoachingLogPanel } from './CoachingLogPanel'
import { DrivingReportPanel } from './DrivingReportPanel'

// The two new blocks on the driver record, behind tabs.
//
// WHY TABS AND NOT TWO MORE SECTIONS. The driver record is already a long scroll:
// manager, performance, banking, documents, confirmations, contract, interviews.
// Sixty-six profile fields and a full coaching history added to that scroll would
// bury the two things this screen already does well. Two tabs inside one block
// keeps both reachable and leaves the rest of the page as it is, which also keeps
// the change to ApplicationDetail.tsx to four lines.
//
// COACHING IS THE DEFAULT TAB, deliberately. It is the one with a cadence, it is
// the one a manager opens with something to record, and the profile is a thing you
// fill in over months rather than on a Tuesday.
//
// DRIVING, 30 September 2026: the clause 21.7 weekly event reports and where the
// driver stands on the clause 21.6 ladder (db/migrations/0369 to 0374). A third
// tab rather than a section, for the same reason as the other two.

type Tab = 'coaching' | 'profile' | 'driving'

const TABS: { key: Tab; label: string; icon: typeof MessageSquare }[] = [
  { key: 'coaching', label: 'Coaching', icon: MessageSquare },
  { key: 'profile', label: 'Mackay 66', icon: UserSearch },
  { key: 'driving', label: 'Driving', icon: Siren },
]

export function KnowYourDriverPanel({ applicationId }: { applicationId: string }) {
  const [tab, setTab] = useState<Tab>('coaching')

  return (
    <section className="rounded-lg border border-border bg-card p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">Knowing this driver</h3>
        <div className="flex gap-1 rounded-md bg-muted p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 text-xs font-medium transition-colors',
                tab === t.key
                  ? 'bg-background text-foreground shadow-retool-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Both mounted only when selected. Each fetches on mount, and the profile
          read is sixty-six rows plus a consent history; loading it behind a tab
          nobody opened would cost every driver record two extra round trips on a
          pool that is sixty wide. */}
      {tab === 'coaching' ? (
        <CoachingLogPanel applicationId={applicationId} />
      ) : tab === 'driving' ? (
        <DrivingReportPanel applicationId={applicationId} />
      ) : (
        <Mackay66Panel applicationId={applicationId} />
      )}
    </section>
  )
}
